<template>
  <div class="sketch-mirror-entities-property" data-testid="sketch-mirror-entities-property-section">
    <n-collapse :default-expanded-names="['info', 'options']" class="pm-collapse">
      <n-collapse-item title="信息" name="info">
        <n-alert type="info" :show-icon="false">
          选择要镜向的实体及镜向基准，然后选择是否保留原实体。
        </n-alert>
      </n-collapse-item>

      <n-collapse-item title="选项(P)" name="options">
        <div class="section-body">
          <span class="field-label">要镜向的实体:</span>
          <PuppyInputSelect
            :active="isEntitiesSelectionActive"
            :items="selectedItems"
            :selected-indices="selectedItems.map((_, index) => index)"
            :active-index="selectedItems.length > 0 ? 0 : -1"
            item-key-field="id"
            item-label-field="label"
            selection-behavior="ctrl-multiple"
            empty-text="请选择要镜向的草图实体"
            delete-label="取消选择"
            delete-all-label="全部取消"
            @update:active="handleEntitiesActiveUpdate"
            @delete-item="handleDeleteEntity"
            @delete-all="handleDeleteAllEntities"
          />

          <n-checkbox
            :checked="copyEnabled"
            data-testid="mirror-copy-checkbox"
            @update:checked="handleCopyEnabledChange"
          >
            复制(C)
          </n-checkbox>

          <span class="field-label">镜向轴:</span>
          <PuppyInputSelect
            class="mirror-axis-input"
            :active="isMirrorAxisSelectionActive"
            :items="mirrorAxisItems"
            :active-index="mirrorAxisItems.length > 0 ? 0 : -1"
            :selected-indices="mirrorAxisItems.length > 0 ? [0] : []"
            :visible-rows="1"
            item-key-field="id"
            item-label-field="label"
            selection-behavior="single"
            empty-text="请选择直线、线性模型边、基准面或平面模型面"
            delete-label="清除镜向轴"
            :show-delete-all-action="false"
            @update:active="handleMirrorAxisActiveUpdate"
            @delete-item="handleClearMirrorAxis"
          />
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { Subscription } from 'rxjs';
import { NAlert, NCheckbox, NCollapse, NCollapseItem } from 'naive-ui';
import {
  SketchEntitiesTransformCommand,
  SketchEntitiesTransformInputMode,
  SketchEntitiesTransformRelationRetentionMode,
  SketchEntitiesTransformSelectionRole,
  type PropertyManagerContext,
  type SketchEntitiesTransformState,
} from '@/core';
import PuppyInputSelect from '@/components/PuppyInputSelect/PuppyInputSelect.vue';
import { useViewer } from '@/hooks';
import {
  SketchEntitiesTransformSelectionRole as CadSketchEntitiesTransformSelectionRole,
  type SketchEntitiesTransformControllerSnapshot,
} from '@puppycad/cad-core';

const props = defineProps<{ readonly context: PropertyManagerContext }>();
const { getViewer, setViewerCallback } = useViewer();

/** 当前 cad-core 镜向命令快照。 */
const localSnapshot = ref<SketchEntitiesTransformControllerSnapshot | null>(null);
/** cad-core 快照订阅。 */
let snapshotSubscription: Subscription | null = null;
/** Viewer 就绪回调取消函数。 */
let disposeViewerReadyCallback: (() => void) | null = null;

/** 当前已选实体。 */
const selectedItems = computed(() => (
  localSnapshot.value?.selectedItems
  ?? props.context.sketchEntitiesTransformState?.selectedItems
  ?? []
));
/** 当前镜向轴单项列表。 */
const mirrorAxisItems = computed(() => (
  localSnapshot.value?.mirrorAxis ? [localSnapshot.value.mirrorAxis] : []
));
/** 是否保留原实体并创建镜向副本。 */
const copyEnabled = computed(() => localSnapshot.value?.copyEnabled ?? true);
/** “要镜向的实体”是否正在接收选择。 */
const isEntitiesSelectionActive = computed(() => (
  (localSnapshot.value?.activeSelectionRole ?? CadSketchEntitiesTransformSelectionRole.Entities)
    === CadSketchEntitiesTransformSelectionRole.Entities
));
/** “镜向轴”是否正在接收选择。 */
const isMirrorAxisSelectionActive = computed(() => (
  localSnapshot.value?.activeSelectionRole === CadSketchEntitiesTransformSelectionRole.MirrorAxis
));

/**
 * 将 cad-core 控制器快照映射为 PropertyManager 状态。
 *
 * @param snapshot cad-core 镜向控制器快照。
 * @returns PropertyManager 可消费的纯数据状态。
 */
function mapSnapshotToState(
  snapshot: SketchEntitiesTransformControllerSnapshot,
): SketchEntitiesTransformState {
  return {
    active: snapshot.active,
    command: SketchEntitiesTransformCommand.Mirror,
    inputMode: SketchEntitiesTransformInputMode.FromTo,
    activeSelectionRole: snapshot.activeSelectionRole === CadSketchEntitiesTransformSelectionRole.MirrorAxis
      ? SketchEntitiesTransformSelectionRole.MirrorAxis
      : SketchEntitiesTransformSelectionRole.Entities,
    relationRetentionMode: SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations,
    selectedItems: snapshot.selectedItems.map((item) => ({
      id: item.id,
      label: item.label,
    })),
    fromPoint: null,
    toPoint: null,
    rotationCenter: null,
    targetX: null,
    targetY: null,
    angleDeg: null,
    canConfirm: snapshot.canConfirm,
    canRepeat: false,
  };
}

/** 从 Viewer 同步当前镜向快照。 */
function syncFromViewer(): void {
  const snapshot = getViewer()?.getSketchMirrorEntitiesSnapshot?.() ?? null;
  localSnapshot.value = snapshot;
  if (snapshot) {
    props.context.syncSketchEntitiesTransformState?.(mapSnapshotToState(snapshot));
  }
}

/** 建立镜向快照订阅。 */
function bindViewerSnapshot(): void {
  snapshotSubscription?.unsubscribe();
  snapshotSubscription = getViewer()?.observeSketchMirrorEntitiesSnapshot?.().subscribe((snapshot) => {
    localSnapshot.value = snapshot;
    props.context.syncSketchEntitiesTransformState?.(mapSnapshotToState(snapshot));
  }) ?? null;
}

onMounted(() => {
  syncFromViewer();
  bindViewerSnapshot();
  disposeViewerReadyCallback = setViewerCallback(() => {
    syncFromViewer();
    bindViewerSnapshot();
  });
});

onBeforeUnmount(() => {
  snapshotSubscription?.unsubscribe();
  snapshotSubscription = null;
  disposeViewerReadyCallback?.();
  disposeViewerReadyCallback = null;
});

/**
 * 切换“要镜向的实体”选择框。
 *
 * @param active 是否激活。
 */
function handleEntitiesActiveUpdate(active: boolean): void {
  if (!active) {
    return;
  }
  getViewer()?.setSketchMirrorEntitiesSelectionRole?.(CadSketchEntitiesTransformSelectionRole.Entities);
  syncFromViewer();
}

/**
 * 切换“镜向轴”选择框。
 *
 * @param active 是否激活。
 */
function handleMirrorAxisActiveUpdate(active: boolean): void {
  if (!active) {
    return;
  }
  getViewer()?.setSketchMirrorEntitiesSelectionRole?.(CadSketchEntitiesTransformSelectionRole.MirrorAxis);
  syncFromViewer();
}

/**
 * 更新复制选项并刷新预览。
 *
 * @param enabled 是否保留原实体并创建镜向副本。
 */
function handleCopyEnabledChange(enabled: boolean): void {
  getViewer()?.setSketchMirrorEntitiesCopyEnabled?.(enabled);
  syncFromViewer();
}

/**
 * 移除单个要镜向的实体。
 *
 * @param payload PuppyInputSelect 删除事件。
 */
function handleDeleteEntity(payload: { item: unknown; index: number }): void {
  const entityId = (payload.item as { id?: string } | null)?.id;
  if (!entityId) {
    return;
  }
  getViewer()?.removeSketchMirrorEntitiesSelection?.(entityId);
  syncFromViewer();
}

/** 清空全部要镜向的实体。 */
function handleDeleteAllEntities(): void {
  for (const item of selectedItems.value) {
    getViewer()?.removeSketchMirrorEntitiesSelection?.(item.id);
  }
  syncFromViewer();
}

/** 清除当前镜向轴。 */
function handleClearMirrorAxis(): void {
  getViewer()?.clearSketchMirrorEntitiesAxis?.();
  syncFromViewer();
}
</script>

<style scoped>
.sketch-mirror-entities-property {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  color: var(--n-text-color-2);
  font-size: 12px;
}

.mirror-axis-input {
  min-height: 44px;
}
</style>
